const {registerModule} = window;

// Matches compiled query('l') / dimensions desktop (1024px); featured large-card interior band
const DESKTOP_FEATURED_MQ = '(min-width: 1024px)';

class TrainingResourceTags {
  constructor(element) {
    this.element = element;
    this.popoverPortaled = false;
    this.initialize();
  }

  initialize() {
    this.bindUI();
    this.bindEvents();
    this.layout();
    requestAnimationFrame(() => {
      this.layout();
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.layout();
      });
    }
  }

  bindUI() {
    this.ui = {};
    this.ui.$tagList = this.element.querySelector('[data-ref="tagList"]');
    this.ui.$overflowTrigger = this.element.querySelector(
      '[data-ref="overflowTrigger"]',
    );
    this.ui.$overflowCount = this.element.querySelector(
      '[data-ref="overflowCount"]',
    );
    this.ui.$popover = this.element.querySelector('[data-ref="popover"]');
    this.ui.$popoverList = this.element.querySelector(
      '[data-ref="popoverList"]',
    );
    this.ui.$tagItems = this.ui.$tagList.querySelectorAll(
      '.training-resource-tags__item:not(.training-resource-tags__item--overflow)',
    );
    this.desktopFeaturedMq = window.matchMedia(DESKTOP_FEATURED_MQ);
    this.canHover = window.matchMedia('(hover: hover)').matches;
  }

  bindEvents() {
    this.handleResize = this.debounce(() => {
      this.layout();
    }, 100);

    window.addEventListener('resize', this.handleResize);
    this.desktopFeaturedMq.addEventListener('change', this.handleResize);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.ui.$tagList);

    this.hidePopover = () => {
      this.ui.$popover.hidden = true;
      this.ui.$overflowTrigger.setAttribute('aria-expanded', 'false');
    };

    this.ui.$overflowTrigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.positionPopover();
    });

    if (this.canHover) {
      this.ui.$overflowTrigger.addEventListener('mouseenter', () => {
        this.positionPopover();
      });
      this.ui.$popover.addEventListener('mouseenter', () => {
        this.positionPopover();
      });
      this.ui.$overflowTrigger.addEventListener('mouseleave', this.hidePopover);
      this.ui.$popover.addEventListener('mouseleave', this.hidePopover);
    }

    this.ui.$overflowTrigger.addEventListener('focus', () => {
      this.positionPopover();
    });

    this.ui.$overflowTrigger.addEventListener('blur', () => {
      window.requestAnimationFrame(() => {
        const active = document.activeElement;

        if (
          this.ui.$popover.hidden ||
          this.ui.$overflowTrigger === active ||
          this.ui.$popover.contains(active)
        ) {
          return;
        }

        this.hidePopover();
      });
    });

    this.onDocumentKeyDown = (event) => {
      if (event.key !== 'Escape' || this.ui.$popover.hidden) {
        return;
      }

      event.preventDefault();
      this.hidePopover();
    };

    document.addEventListener('keydown', this.onDocumentKeyDown);

    this.onDocumentPointerDown = (event) => {
      if (this.ui.$popover.hidden) {
        return;
      }

      const target = event.target;
      const inWrap = this.element.contains(target);
      const inPopover = this.ui.$popover.contains(target);

      if (!inWrap && !inPopover) {
        this.hidePopover();
      }
    };

    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    this.popoverRepositionFrame = null;
    this.repositionPopoverIfOpen = () => {
      if (this.ui.$popover.hidden) {
        return;
      }

      if (this.popoverRepositionFrame) {
        return;
      }

      this.popoverRepositionFrame = window.requestAnimationFrame(() => {
        this.popoverRepositionFrame = null;

        if (!this.ui.$popover.hidden) {
          this.positionPopover();
        }
      });
    };

    window.addEventListener('scroll', this.repositionPopoverIfOpen, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', this.repositionPopoverIfOpen, {
      passive: true,
    });
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  }

  getMaxLines() {
    const card = this.element.closest('.resourceCard');
    const inFeatured = this.element.closest('.post-list-featured');
    const inCarousel = this.element.closest(
      '.post-list-featured__carousel-list',
    );
    const isLarge = card && card.classList.contains('resourceCard--large');

    if (inCarousel) {
      return 4;
    }

    if (isLarge && inFeatured) {
      return this.desktopFeaturedMq.matches ? 1 : 2;
    }

    return 3;
  }

  countLines() {
    const items = this.ui.$tagList.querySelectorAll(
      '.training-resource-tags__item:not([hidden])',
    );
    const tops = new Set();

    items.forEach((item) => {
      tops.add(item.offsetTop);
    });

    return tops.size;
  }

  setVisibleTags(visibleCount, showOverflow) {
    this.ui.$tagItems.forEach((item, index) => {
      item.hidden = index >= visibleCount;
    });
    this.ui.$overflowTrigger.closest(
      '.training-resource-tags__item--overflow',
    ).hidden = !showOverflow;
  }

  layout() {
    const maxLines = this.getMaxLines();
    const totalTags = this.ui.$tagItems.length;

    this.element.classList.remove('has-tag-overflow');
    this.hidePopover();
    this.ui.$popoverList.textContent = '';

    if (totalTags === 0) {
      return;
    }

    let visibleCount = totalTags;

    while (visibleCount >= 0) {
      const hiddenCount = totalTags - visibleCount;

      if (hiddenCount === 0) {
        this.setVisibleTags(visibleCount, false);
        if (this.countLines() <= maxLines) {
          break;
        }
      } else {
        this.ui.$overflowCount.textContent = `+${hiddenCount}`;
        this.setVisibleTags(visibleCount, true);
        if (this.countLines() <= maxLines) {
          this.element.classList.add('has-tag-overflow');
          this.populatePopover(visibleCount);
          break;
        }
      }

      visibleCount -= 1;
    }

    if (visibleCount < 0) {
      this.setVisibleTags(totalTags, false);
    }
  }

  populatePopover(firstHiddenIndex) {
    this.ui.$popoverList.textContent = '';

    for (let i = firstHiddenIndex; i < this.ui.$tagItems.length; i += 1) {
      const tagText = this.ui.$tagItems[i].textContent.trim();
      const item = document.createElement('li');
      item.className = 'training-resource-tags__item';
      item.textContent = tagText;
      this.ui.$popoverList.appendChild(item);
    }
  }

  ensurePopoverInBody() {
    const popover = this.ui.$popover;

    if (popover.parentElement !== document.body) {
      document.body.appendChild(popover);
      this.popoverPortaled = true;
    }
  }

  positionPopover() {
    if (!this.element.classList.contains('has-tag-overflow')) {
      return;
    }

    const trigger = this.ui.$overflowTrigger;
    const popover = this.ui.$popover;
    const rect = trigger.getBoundingClientRect();

    this.ensurePopoverInBody();
    popover.hidden = false;
    popover.style.position = 'fixed';
    popover.style.left = `${rect.left + rect.width / 2}px`;
    popover.style.top = `${rect.bottom + 8}px`;
    popover.style.transform = 'translateX(-50%)';
    popover.style.zIndex = '1000';
    this.ui.$overflowTrigger.setAttribute('aria-expanded', 'true');
  }
}

registerModule('TrainingResourceTags', TrainingResourceTags);
